import React, { useEffect, useRef, useState } from "react";
import { IoHome, IoCubeOutline, IoBagOutline, IoMenuOutline } from "react-icons/io5";
import { useLocation, useNavigate } from "react-router-dom";

const ProductCard = ({ product }) => (
  <div className="flex items-center gap-3 border-b border-gray-100 py-3">
    <img
      loading="lazy"
      src={product.productImages && product.productImages[0] ? product.productImages[0] : '/placeholder.png'}
      alt={product.productName}
      className="h-14 w-14 rounded-lg object-cover"
    />
    <div className="flex-1">
      <p className="text-sm text-gray-900 text-left">{product.productName}</p>
      <p className="text-sm font-bold text-gray-900 text-left">₦{Number(product.productSellingPrice || 0).toLocaleString("en-NG")}</p>
      <p className="text-sm text-gray-600 text-left">{product.productCategory}</p>
    </div>
    <button className="rounded-lg bg-[#19C463] px-4 py-2 text-xs font-semibold text-white">
      Edit
    </button>
  </div>
);

const StatCard = ({ title, value, suffix }) => (
  <div className="flex-1 rounded-lg bg-white p-4 shadow-[0px_1px_4px_rgba(0,0,0,0.5)]">
    <p className="text-sm text-gray-600">{title}</p>
    <p className="mt-2 truncate text-lg font-bold text-gray-900">
      {value} {suffix}
    </p>
  </div>
);

const VendorHomeScreen = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [activePeriod, setActivePeriod] = useState("7 days");
  const [loginSuccessMessage, setLoginSuccessMessage] = useState(location.state && location.state.loginSuccess ? location.state.loginSuccess : "");
  const [storeName, setStoreName] = useState("");
  const [notifications, setNotifications] = useState([]);
  const notificationTimersRef = useRef({});

  React.useEffect(() => {
    // Fetch vendor profile
    const fetchProfile = async () => {
      try {
        const token = localStorage.getItem("vendorToken");
        const headers = { "Content-Type": "application/json" };
        if (token) headers["Authorization"] = `Bearer ${token}`;
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/vendors/profile`, {
          method: "GET",
          headers,
        });
        if (response.ok) {
          const data = await response.json();
          if (data && data.data && data.data.storeName) {
            setStoreName(data.data.storeName);
          } else {
            setStoreName("");
          }
        } else {
          setStoreName("");
        }
      } catch {
        setStoreName("");
      }
    };
    fetchProfile();
    // Fetch vendor products (first 2 live)
    const fetchProducts = async () => {
      try {
        const token = localStorage.getItem("vendorToken");
        const headers = { "Content-Type": "application/json" };
        if (token) headers["Authorization"] = `Bearer ${token}`;
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/vendors/products?status=live`, {
          method: "GET",
          headers,
        });
        if (response.ok) {
          const data = await response.json();
          if (data && data.data && Array.isArray(data.data)) {
            setProducts(data.data.slice(0, 2));
          } else {
            setProducts([]);
          }
        } else {
          setProducts([]);
        }
      } catch {
        setProducts([]);
      }
    };
    fetchProducts();
  }, []);

  React.useEffect(() => {
    if (loginSuccessMessage) {
      const timer = setTimeout(() => setLoginSuccessMessage(""), 2000);
      return () => clearTimeout(timer);
    }
  }, [loginSuccessMessage]);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const token = localStorage.getItem("vendorToken");
        if (!token) return;
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/vendors/notifications`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          if (data && Array.isArray(data.data)) {
            setNotifications(data.data);
          } else {
            setNotifications([]);
          }
        } else {
          setNotifications([]);
        }
      } catch {
        setNotifications([]);
      }
    };
    fetchNotifications();
  }, []);

  const markNotificationRead = async (notificationId) => {
    try {
      const token = localStorage.getItem("vendorToken");
      if (!token) return;
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/vendors/notifications/${notificationId}/read`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (!response.ok || !data.success) return;
      setNotifications((prev) => prev.filter((item) => item._id !== notificationId));
      if (notificationTimersRef.current[notificationId]) {
        clearTimeout(notificationTimersRef.current[notificationId]);
        delete notificationTimersRef.current[notificationId];
      }
    } catch {
      // leave notification if request fails
    }
  };

  useEffect(() => {
    notifications.forEach((notification) => {
      const id = notification._id || notification.id;
      const isUnread =
        notification.isRead === false ||
        notification.read === false ||
        notification.unread === true ||
        notification.isRead === undefined;
      if (!id || !isUnread || notificationTimersRef.current[id]) return;
      notificationTimersRef.current[id] = setTimeout(() => {
        markNotificationRead(id);
      }, 6000);
    });
    return () => {
      Object.values(notificationTimersRef.current).forEach((timer) => clearTimeout(timer));
      notificationTimersRef.current = {};
    };
  }, [notifications]);

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {loginSuccessMessage && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 bg-[#19C463] text-white px-6 py-3 rounded-lg shadow-lg text-sm font-semibold animate-fadeInOut" style={{ minWidth: '220px', textAlign: 'center', letterSpacing: '0.5px' }}>
          {loginSuccessMessage}
        </div>
      )}
      <header className="fixed top-0 left-0 right-0 z-10 bg-white px-6 py-4" style={{ borderBottom: "4px solid #000000" }}>
        <h1 className="text-lg font-semibold text-gray-900">
          Welcome <span className="text-[#19C463]">{storeName}</span>
        </h1>
      </header>

      <main className="px-6 pt-20">
        {notifications.length > 0 && (
          <section className="mt-6 rounded-lg bg-white p-4 shadow-sm">
            <h2 className="text-base font-semibold text-gray-900">Notifications</h2>
            <div className="mt-4">
              <div className="space-y-3">
                {notifications.map((notification) => {
                  const id = notification._id || notification.id;
                  const isUnread =
                    notification.isRead === false ||
                    notification.read === false ||
                    notification.unread === true ||
                    notification.isRead === undefined;
                  const timeText = notification.createdAt
                    ? new Date(notification.createdAt).toLocaleTimeString()
                    : "";
                  return (
                    <div
                      key={id}
                      className="flex items-start justify-between gap-3 rounded-lg border border-gray-100 p-3"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          {isUnread && <span className="h-2 w-2 rounded-full bg-[#19C463]" />}
                          <p className={`text-sm text-gray-900 ${isUnread ? "font-semibold" : ""}`}>
                            {notification.message || "Notification"}
                          </p>
                        </div>
                        <p className="mt-1 text-xs text-gray-600">
                          {notification.productName || "Product"} â€¢ {notification.status || "Status"}
                        </p>
                        <p className="mt-1 text-xs text-gray-500">{timeText}</p>
                      </div>
                      <button
                        type="button"
                        className="rounded-md border border-[#19C463] px-3 py-1 text-xs font-semibold text-[#19C463]"
                        onClick={() => markNotificationRead(id)}
                      >
                        Read
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        <section className="mt-6 rounded-lg bg-white p-4 shadow-sm">
          <h2 className="text-base font-semibold text-gray-900">Your Products</h2>
          
          <div className="mt-3 flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Available Products <span className="font-semibold">({products.length})</span>
            </p>
            <button className="text-sm font-medium text-[#19C463]">See all</button>
          </div>

          <div className="mt-4">
            {products.length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-500">
                No available products to show
              </p>
            ) : (
              <div>
                {products.map((product) => (
                  <ProductCard key={product._id || product.id} product={product} />
                ))}
              </div>
            )}
          </div>

          <button
            onClick={() => navigate("/add-products")}
            className="mt-4 w-full rounded-lg bg-[#A3E7C1] py-3 text-sm font-semibold text-[#19C463]"
          >
            {products.length === 0 ? "Add Your First Product" : "Add a new product"}
          </button>
        </section>

        <section className="mt-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-base font-semibold text-gray-900">Store Engagement</h2>
            <div className="flex gap-2 hidden">
              {["7 days", "30 days", "90 days"].map((period) => (
                <button
                  key={period}
                  onClick={() => setActivePeriod(period)}
                  className={`rounded-lg px-3 py-1 text-xs font-medium transition ${
                    activePeriod === period
                      ? "bg-[#19C463] text-white"
                      : "bg-gray-200 text-gray-700"
                  }`}
                >
                  {period}
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-3">
            <StatCard title="Store Income" value="0.00" suffix="NGN" />
            <StatCard title="Product Sold" value="0" suffix="" />
          </div>
        </section>
      </main>

      <nav className="fixed bottom-0 left-0 right-0 flex items-center justify-around border-t border-gray-200 bg-white py-3">
        <button className="flex flex-col items-center gap-1" onClick={() => navigate("/vendor-home")}>
          <IoHome className="h-6 w-6 text-[#19C463]" />
          <span className="text-xs font-bold text-[#19C463]">Home</span>
        </button>
        <button className="flex flex-col items-center gap-1" onClick={() => navigate("/products")}>
          <IoCubeOutline className="h-6 w-6 text-gray-500" />
          <span className="text-xs text-gray-500">Product</span>
        </button>
        <button className="flex flex-col items-center gap-1" onClick={() => navigate("/vendor-orders")}>
          <IoBagOutline className="h-6 w-6 text-gray-500" />
          <span className="text-xs text-gray-500">Orders</span>
        </button>
        <button className="flex flex-col items-center gap-1" onClick={() => navigate("/menu")}>
          <IoMenuOutline className="h-6 w-6 text-gray-500" />
          <span className="text-xs text-gray-500">Menu</span>
        </button>
      </nav>
    </div>
  );
};

export default VendorHomeScreen;


